import { useQuery } from "@tanstack/react-query";
import { useOutletContext, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { jobProgress, fmtDate } from "@/lib/constants";
import ApprovalCard from "@/components/ApprovalCard";
import RequestJobDialog from "@/components/RequestJobDialog";
import ThroughputChart from "@/components/ThroughputChart";
import { Progress } from "@/components/ui/progress";
import { ClipboardList, CheckCircle2, Loader2, Clock, ArrowRight, Inbox } from "lucide-react";

function StatCard({ icon: Icon, label, value, accent, testId }) {
  return (
    <div className="rounded-lg border border-border bg-card p-5 corner-tick" data-testid={testId}>
      <div className="flex items-center justify-between">
        <span className="label-eyebrow">{label}</span>
        <Icon className={`h-5 w-5 ${accent}`} />
      </div>
      <div className="font-heading text-3xl font-extrabold mt-2">{value}</div>
    </div>
  );
}

export default function DashboardPage() {
  const { user } = useOutletContext();
  const { data: stats } = useQuery({ queryKey: ["stats"], queryFn: async () => (await api.get("/dashboard/stats")).data });
  const { data: jobs = [] } = useQuery({ queryKey: ["jobs"], queryFn: async () => (await api.get("/jobs")).data });
  const isManager = user.role === "owner" || user.role === "manager";
  const { data: approvals = [] } = useQuery({
    queryKey: ["approvals"], queryFn: async () => (await api.get("/approvals")).data, enabled: isManager,
  });
  const { data: throughput = [] } = useQuery({
    queryKey: ["throughput"], queryFn: async () => (await api.get("/analytics/stage-throughput")).data, enabled: isManager,
  });

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="label-eyebrow text-primary">Dashboard</div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight">Welcome, {user.name}</h1>
        </div>
        {user.role === "client" && <RequestJobDialog />}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={ClipboardList} label="Total Jobs" value={stats?.total_jobs ?? "—"} accent="text-primary" testId="stat-total" />
        <StatCard icon={Loader2} label="In Progress" value={stats?.in_progress_jobs ?? "—"} accent="text-amber-400" testId="stat-progress" />
        <StatCard icon={CheckCircle2} label="Completed" value={stats?.completed_jobs ?? "—"} accent="text-emerald-400" testId="stat-completed" />
        {isManager ? (
          <StatCard icon={Clock} label="Pending Approvals" value={stats?.pending_approvals ?? "—"} accent="text-blue-400" testId="stat-pending" />
        ) : null}
      </div>

      {isManager && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <h2 className="font-heading text-xl font-bold mb-3">Stage Throughput</h2>
            <ThroughputChart data={throughput} />
          </div>
          <div>
            <h2 className="font-heading text-xl font-bold mb-3">Job Requests</h2>
            <Link to="/jobs" className="block rounded-lg border border-border bg-card p-5 hover:border-primary/50 transition-colors" data-testid="job-requests-card">
              <div className="flex items-center justify-between">
                <span className="label-eyebrow">Awaiting activation</span>
                <Inbox className="h-5 w-5 text-secondary" />
              </div>
              <div className="font-heading text-3xl font-extrabold mt-2">{stats?.job_requests ?? "—"}</div>
              <p className="text-xs text-muted-foreground mt-1">Client-submitted requests to review</p>
            </Link>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {isManager && (
          <div className="lg:col-span-1">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-heading text-xl font-bold">Pending Approvals</h2>
              <Link to="/approvals" className="text-sm text-primary hover:underline flex items-center gap-1">
                View all <ArrowRight className="h-3 w-3" />
              </Link>
            </div>
            <div className="space-y-3" data-testid="dashboard-approvals">
              {approvals.length === 0 && (
                <div className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
                  No updates waiting for review.
                </div>
              )}
              {approvals.slice(0, 4).map((u) => <ApprovalCard key={u.id} update={u} />)}
            </div>
          </div>
        )}

        <div className={isManager ? "lg:col-span-2" : "lg:col-span-3"}>
          <h2 className="font-heading text-xl font-bold mb-3">Recent Jobs</h2>
          <div className="space-y-3" data-testid="dashboard-jobs">
            {jobs.length === 0 && (
              <div className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
                No jobs yet.
              </div>
            )}
            {jobs.slice(0, 6).map((j) => {
              const prog = jobProgress(j);
              return (
                <Link key={j.id} to={`/jobs/${j.id}`} data-testid={`dashboard-job-${j.id}`}
                  className="block rounded-lg border border-border bg-card p-4 hover:border-primary/50 transition-colors">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <span className="font-mono-tag text-xs text-primary">{j.job_number}</span>
                      <div className="font-heading font-semibold truncate">{j.client_name}</div>
                      <div className="text-sm text-muted-foreground truncate">{j.ms_description}</div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-heading text-lg font-bold">{prog}%</div>
                      <div className="text-xs text-muted-foreground">{j.current_stage}</div>
                    </div>
                  </div>
                  <Progress value={prog} className="mt-3 h-1.5" />
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
